import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PRODUCTS } from "@/lib/data";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products", hasDropdown: true },
  { to: "/business", label: "For Business Owners" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
];

const ProductsMenu = () => (
  <div
    data-testid="products-dropdown"
    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(680px,calc(100vw-2rem))] opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200"
  >
    <div className="rounded-3xl bg-white shadow-2xl shadow-[#0B132B]/15 border border-[#0B132B]/5 overflow-hidden">
      <div className="px-6 py-5 border-b border-[#0B132B]/8 bg-[#F9F6F0]">
        <p className="text-xs tracking-[0.2em] uppercase text-[#29ABE2] font-semibold">
          Our products
        </p>
        <p className="mt-1 text-sm text-[#334155] font-light">
          Six core strategies. Pick yours, or let us recommend.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-1 p-3">
        {PRODUCTS.map((p) => {
          const Icon = p.icon;
          return (
            <Link
              key={p.id}
              to={`/products/${p.id}`}
              data-testid={`dropdown-product-${p.id}`}
              className="group/item flex items-start gap-3 rounded-2xl p-4 transition-colors duration-200 hover:bg-[#F9F6F0]"
            >
              <span className="w-10 h-10 rounded-xl bg-[#29ABE2]/10 text-[#0a6fc2] flex items-center justify-center shrink-0 group-hover/item:bg-[#29ABE2] group-hover/item:text-white transition-colors">
                <Icon size={18} />
              </span>
              <span className="min-w-0">
                <span className="block font-heading font-bold text-[15px] text-[#0B132B] tracking-tight">
                  {p.name}
                </span>
                <span className="block text-xs text-[#334155]/80 font-light mt-0.5 line-clamp-2">
                  {p.short}
                </span>
              </span>
            </Link>
          );
        })}
      </div>
      <Link
        to="/products"
        data-testid="dropdown-all-products"
        className="flex items-center justify-between px-6 py-4 border-t border-[#0B132B]/8 bg-[#F9F6F0]/50 text-sm font-semibold text-[#0B132B] hover:bg-[#F9F6F0] transition-colors"
      >
        Compare all products
        <ArrowRight size={15} />
      </Link>
    </div>
  </div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F9F6F0]/85 border-b border-[#0B132B]/5">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) =>
            l.hasDropdown ? (
              <div key={l.to} className="relative group">
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                      isActive || pathname.startsWith("/products")
                        ? "text-[#0B132B] bg-[#0B132B]/5"
                        : "text-[#334155] hover:text-[#0B132B] hover:bg-[#0B132B]/5"
                    }`
                  }
                >
                  {l.label}
                  <ChevronDown
                    size={14}
                    className="transition-transform duration-200 group-hover:rotate-180"
                  />
                </NavLink>
                <ProductsMenu />
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "text-[#0B132B] bg-[#0B132B]/5"
                      : "text-[#334155] hover:text-[#0B132B] hover:bg-[#0B132B]/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/quote"
            data-testid="nav-get-quote-button"
            className="hidden lg:inline-flex items-center gap-2 rounded-full bg-[#0B132B] text-white text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-[#29ABE2] hover:scale-105"
          >
            Get a Quote <ArrowRight size={15} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-full hover:bg-[#0B132B]/5 text-[#0B132B]"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F9F6F0] border-t border-[#0B132B]/5 px-5 pb-6 pt-2 max-h-[calc(100vh-72px)] overflow-y-auto"
        >
          {LINKS.map((l) =>
            l.hasDropdown ? (
              <div key={l.to}>
                <button
                  type="button"
                  data-testid="mobile-products-toggle"
                  onClick={() => setProductsOpen(!productsOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl text-base font-medium text-[#334155]"
                >
                  <span>{l.label}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform duration-200 ${
                      productsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {productsOpen && (
                  <div className="pl-3 pb-2">
                    {PRODUCTS.map((p) => (
                      <Link
                        key={p.id}
                        to={`/products/${p.id}`}
                        data-testid={`mobile-dropdown-product-${p.id}`}
                        className="block px-4 py-2.5 text-sm text-[#334155] font-light hover:text-[#29ABE2]"
                      >
                        {p.name}
                      </Link>
                    ))}
                    <Link
                      to="/products"
                      className="block px-4 py-2.5 text-sm text-[#0a6fc2] font-semibold"
                    >
                      View all products →
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-2xl text-base font-medium ${
                    isActive ? "text-[#0B132B] bg-[#0B132B]/5" : "text-[#334155]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to="/quote"
            data-testid="mobile-nav-get-quote-button"
            className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#0B132B] text-white font-semibold px-6 py-3.5"
          >
            Get a Quote <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </header>
  );
};
